import React from 'react';
import { recordRuntimeDiagnosticEvent, sanitizeError } from './runtimeDiagnostics';

type Props = {
  routeType: 'reserve' | 'manage';
  children: React.ReactNode;
};

type State = { hasError: boolean; message: string | null };

export class PublicRouteDiagnosticsBoundary extends React.Component<Props, State> {
  state: State = { hasError: false, message: null };

  static getDerivedStateFromError(error: unknown): State {
    const safe = sanitizeError(error);
    return { hasError: true, message: safe.errorMessage || 'Public route rendering failed.' };
  }

  componentDidCatch(error: unknown, errorInfo: React.ErrorInfo) {
    const safe = sanitizeError(error);
    recordRuntimeDiagnosticEvent({
      event: 'public-route:error-boundary:caught',
      routeType: this.props.routeType,
      errorName: safe.errorName,
      errorMessage: safe.errorMessage,
      isAbortLike: safe.isAbortLike,
      componentStack: errorInfo.componentStack || null,
    });
    if ((import.meta as any).env?.DEV) {
      console.error('[PublicRouteDiagnosticsBoundary] caught public route error', { routeType: this.props.routeType, error, componentStack: errorInfo.componentStack });
    }
  }

  render() {
    if (!this.state.hasError) return this.props.children;
    return (
      <div className="fixed inset-0 flex items-center justify-center bg-gray-50 p-4">
        <div className="max-w-md rounded-2xl bg-white p-6 text-center shadow">
          <h1 className="text-xl font-semibold text-red-700">Reservation page failed to load</h1>
          <p className="mt-2 text-sm text-gray-600">A safe runtime diagnostic was recorded. Please refresh the page or contact support.</p>
          {(import.meta as any).env?.DEV && this.state.message ? <p className="mt-3 text-xs text-gray-500">{this.state.message}</p> : null}
        </div>
      </div>
    );
  }
}
